/**
 * kitten_text.ts — PURE text side of the KittenTTS pipeline (versona voices,
 * 2026-09-10).
 *
 * Mirrors `kittentts/onnx_model.py` (KittenML/KittenTTS, Apache-2.0) so the
 * browser feeds the ONNX model exactly what the reference does:
 *   · the symbol table (`$` pad, punctuation, letters, IPA — 178 symbols;
 *     index 10 is the ellipsis, the model's end-of-utterance token),
 *   · `basic_english_tokenize` — split the phoneme string on `\w+|[^\w\s]`
 *     and re-join with single spaces (so punctuation stands apart),
 *   · the id sequence `[0, …tokens, 10, 0]`,
 *   · the style row `min(len(text), rows − 1)` of the voice's [400, 256]
 *     embedding table,
 *   · sentence chunking (`chunk_text`: sentence boundaries, 400-char cap,
 *     a trailing period ensured) and a light `normalize` (numbers, the
 *     credits sign, percentages) — the reference pipeline's essentials.
 * Phonemization itself (espeak-ng, en-us, stress + punctuation kept) is the
 * `phonemizer` package in the worker; this file never touches it.
 */

const PAD = '$';
const PUNCTUATION = ';:,.!?¡¿—…"«»"" ';
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const LETTERS_IPA = 'ɑɐɒæɓʙβɔɕçɗɖðʤəɘɚɛɜɝɞɟʄɡɠɢʛɦɧħɥʜɨɪʝɭɬɫɮʟɱɯɰŋɳɲɴøɵɸθœɶʘɹɺɾɻʀʁɽʂʃʈʧʉʊʋⱱʌɣɤʍχʎʏʑʐʒʔʡʕʢǀǁǂǃˈˌːˑʼʴʰʱʲʷˠˤ˞↓↑→↗↘\'̩\'ᵻ';

/** The model's symbol table, in index order. */
export const KITTEN_SYMBOLS: readonly string[] = [PAD, ...PUNCTUATION, ...LETTERS, ...LETTERS_IPA];
const INDEX = new Map<string, number>();
KITTEN_SYMBOLS.forEach((s, i) => { if (!INDEX.has(s)) INDEX.set(s, i); });

/** The end-of-utterance token the reference appends (the ellipsis symbol). */
export const KITTEN_END_TOKEN = 10;
/** Output sample rate (Hz). */
export const KITTEN_SAMPLE_RATE = 24000;
/** The reference drops this many trailing samples of every chunk. */
export const KITTEN_TRIM_SAMPLES = 5000;
/** Style embedding width. */
export const KITTEN_STYLE_DIM = 256;

/** `basic_english_tokenize` + join: words and punctuation marks separated by single spaces. */
export function spacePhonemes(phonemes: string): string {
  return (phonemes.match(/[\p{L}\p{N}_̀-ͯʰ-˿ᴀ-᷿]+|[^\p{L}\p{N}_\s̀-ͯʰ-˿ᴀ-᷿]/gu) ?? []).join(' ');
}

/** Phoneme string → model ids `[0, …, 10, 0]` (unknown characters are dropped, as the reference does). */
export function tokenize(phonemes: string): number[] {
  const ids = [0];
  for (const ch of spacePhonemes(phonemes)) {
    const i = INDEX.get(ch);
    if (i !== undefined) ids.push(i);
  }
  ids.push(KITTEN_END_TOKEN, 0);
  return ids;
}

/** Which style row a text of this length uses (the reference's `ref_id`). */
export function styleRow(textLength: number, rows: number): number {
  return Math.max(0, Math.min(textLength, rows - 1));
}

// ─── normalisation (the reference's essentials) ──────────────────────────────

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function threeDigits(n: number): string {
  const h = Math.floor(n / 100), r = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h]} hundred`);
  if (r) parts.push(r < 20 ? ONES[r] : `${TENS[Math.floor(r / 10)]}${r % 10 ? `-${ONES[r % 10]}` : ''}`);
  return parts.join(' ');
}

/** An integer in words (up to the billions; beyond, digits are read out). */
export function numberToWords(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (n < 0) return `minus ${numberToWords(-n)}`;
  if (n === 0) return 'zero';
  if (n >= 1e12) return String(n).split('').map((d) => ONES[Number(d)] ?? d).join(' ');
  const scales: Array<[number, string]> = [[1e9, 'billion'], [1e6, 'million'], [1e3, 'thousand']];
  const parts: string[] = [];
  let rest = Math.floor(n);
  for (const [v, name] of scales) {
    if (rest >= v) { parts.push(`${threeDigits(Math.floor(rest / v))} ${name}`); rest %= v; }
  }
  if (rest) parts.push(threeDigits(rest));
  return parts.join(' ');
}

/**
 * The world's text made readable: the credits sign, percentages, decimals
 * and integers become words; stray markup and the link brackets go; long
 * runs of whitespace collapse.
 */
export function normalizeText(text: string): string {
  let t = text.replace(/\s+/g, ' ').trim();
  t = t.replace(/ⓒ\s*(\d[\d,]*)/g, (_, d: string) => `${numberToWords(Number(d.replace(/,/g, '')))} credits`);
  t = t.replace(/(\d[\d,]*)\s*ⓒ/g, (_, d: string) => `${numberToWords(Number(d.replace(/,/g, '')))} credits`);
  t = t.replace(/ⓒ/g, ' credits ');
  t = t.replace(/(\d+(?:\.\d+)?)\s*%/g, (_, d: string) => `${decimalToWords(d)} percent`);
  t = t.replace(/(\d+)\.(\d+)/g, (_, a: string, b: string) => `${numberToWords(Number(a))} point ${b.split('').map((x) => ONES[Number(x)]).join(' ')}`);
  t = t.replace(/\d[\d,]*/g, (d) => numberToWords(Number(d.replace(/,/g, ''))));
  t = t.replace(/[<>[\]{}*_`#|]/g, ' ');
  t = t.replace(/\s+([,.;:!?])/g, '$1').replace(/\s+/g, ' ').trim();
  return t;
}

function decimalToWords(d: string): string {
  const [a, b] = d.split('.');
  return b ? `${numberToWords(Number(a))} point ${b.split('').map((x) => ONES[Number(x)]).join(' ')}` : numberToWords(Number(a));
}

/** The reference's `ensure_punctuation`: a chunk ends with a mark. */
export function ensurePunctuation(s: string): string {
  const t = s.trim();
  return /[.!?…,;:]$/.test(t) ? t : `${t}.`;
}

const ABBREV = /\b(?:mr|mrs|ms|dr|st|vs|etc|e\.g|i\.e)\.$/i;

/**
 * `chunk_text`: split at sentence ends (a period, question or exclamation
 * mark, ellipsis — not after common abbreviations), then cap each sentence at
 * `maxLen` characters on word boundaries; every chunk ends with punctuation.
 */
export function chunkText(text: string, maxLen = 400): string[] {
  const sentences: string[] = [];
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch !== '.' && ch !== '!' && ch !== '?' && ch !== '…') continue;
    const next = text[i + 1];
    if (next !== undefined && !/\s/.test(next)) continue;         // 3.14, e.g.x
    if (ch === '.' && ABBREV.test(text.slice(start, i + 1).trimEnd())) continue;
    sentences.push(text.slice(start, i + 1));
    start = i + 1;
  }
  if (start < text.length) sentences.push(text.slice(start));
  const chunks: string[] = [];
  for (const raw of sentences) {
    const s = raw.trim();
    if (!s) continue;
    if (s.length <= maxLen) { chunks.push(ensurePunctuation(s)); continue; }
    let cur = '';
    for (const w of s.split(/\s+/)) {
      if (cur.length + w.length + 1 <= maxLen) cur = cur ? `${cur} ${w}` : w;
      else { if (cur) chunks.push(ensurePunctuation(cur)); cur = w; }
    }
    if (cur) chunks.push(ensurePunctuation(cur));
  }
  return chunks;
}

/** Everything the worker needs from a paragraph: normalised, chunked. */
export function prepareUtterance(text: string): string[] {
  return chunkText(normalizeText(text));
}
